import React, { useEffect, useRef } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { ThemeProvider } from "../provider/ThemeProvider";
import { SidebarProvider } from "@/components/ui/sidebar";
import DashboardWrapper from "@/components/pages/dashboard/DashboardWrapper";
import { DashboardSidebar } from "@/components/pages/dashboard/DashboardSidebar";
import { BreadcrumbsProvider } from "@/components/provider/BreadcrumbsProvider";
import { Toaster } from "react-hot-toast";
import { toast } from "sonner";
import DraggableCallDialog from "@/components/dialer/DraggableCallDialog";
import DraggableMessageDialog from "@/components/messages/DraggableMessageDialog";
import { useCallDialog } from "@/context/CallDialogContext";
import { useMessageDialog } from "@/context/MessageDialogContext";
import { useTwilioDevice } from "@/hooks/useTwilioDevice";
import { useCallSocket } from "@/hooks/useCallSocket";
import { useA2pStatusSocket } from "@/hooks/useA2pStatusSocket";
import { useNotificationSocket } from "@/hooks/useNotificationSocket";
import { useSocketResync } from "@/hooks/useSocketResync";
import { Call } from "@twilio/voice-sdk";
import { IncomingCallEvent } from "@/model/interface/IncomingCallEvent";
import {
  restApi,
  restApiTagTypes,
  useGetCallsQuery,
  useLazyGetIncomingCallQuery,
  useReportCallPlatformMutation,
} from "@/store/rest_api";
import { adminApi, adminApiTagTypes } from "@/store/admin_api";
import type { AppDispatch } from "@/store/store";
import { useDispatch } from "react-redux";
import { PowerDialerProvider } from "@/context/PowerDialerContext";
import { SocketProvider } from "@/context/SocketContext";
import { socketClient } from "@/services/socketClient";
import { DEVICE_PLATFORM } from "@/util/device";
import { HubSpotChatIdentity } from "@/components/provider/HubSpotChatIdentity";

const INCOMING_EVENT_MAX_AGE_MS = 30_000;
const INCOMING_FETCH_GRACE_MS = 1000;
const TERMINAL_CALL_STATUSES: string[] = ["COMPLETED", "FAILED", "MISSED", "NO_ANSWER", "CANCELED", "BUSY"];

const DashboardLayoutInner = () => {
  const location = useLocation();
  const {
    setIncomingCall,
    openCallDialog,
    switchToDialerTab,
    activeCall,
    setActiveCall,
    setDevice,
    heldCall,
    setHeldCall,
    activeCallInfo,
    setActiveCallInfo,
    setDialerDraft,
  } = useCallDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const dialNumber = searchParams.get("dial");
  const { requestCloseMessageDialog } = useMessageDialog();
  const [reportCallPlatform] = useReportCallPlatformMutation();
  const [fetchIncomingCall] = useLazyGetIncomingCallQuery();

  const { refetch: refetchCalls } = useGetCallsQuery({ size: 50 });
  const dispatch = useDispatch<AppDispatch>();
  const pendingTwilioCall = useRef<Call | null>(null);
  const pendingSocketEvent = useRef<IncomingCallEvent | null>(null);
  const pendingSocketEventReceivedAt = useRef(0);
  const heldCallRef = useRef(heldCall);
  const activeCallInfoRef = useRef(activeCallInfo);
  const activeCallRef = useRef(activeCall);

  useEffect(() => {
    heldCallRef.current = heldCall;
  }, [heldCall]);
  useEffect(() => {
    activeCallInfoRef.current = activeCallInfo;
  }, [activeCallInfo]);
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);
  useEffect(() => {
    if (!dialNumber) {
      return;
    }
    setDialerDraft(dialNumber);
    openCallDialog();
    switchToDialerTab();
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("dial");
        return next;
      },
      { replace: true },
    );
  }, [dialNumber]);

  const matchingSocketEvent = (twilioCall: Call): IncomingCallEvent | null => {
    const socketEvent = pendingSocketEvent.current;
    if (!socketEvent) {
      return null;
    }
    const ringCallId = twilioCall.customParameters?.get("callId");
    if (ringCallId) {
      return socketEvent.callId === ringCallId ? socketEvent : null;
    }
    const isRecent = Date.now() - pendingSocketEventReceivedAt.current <= INCOMING_EVENT_MAX_AGE_MS;
    if (!isRecent || socketEvent.fromNumber !== twilioCall.parameters.From) {
      return null;
    }
    return socketEvent;
  };

  const clearPendingIncoming = (twilioCall: Call) => {
    if (pendingTwilioCall.current !== twilioCall) {
      return false;
    }
    if (matchingSocketEvent(twilioCall)) {
      pendingSocketEvent.current = null;
    }
    pendingTwilioCall.current = null;
    return true;
  };

  const rebuildIncomingCard = async (twilioCall: Call, callId: string) => {
    if (pendingTwilioCall.current !== twilioCall || matchingSocketEvent(twilioCall)) {
      return;
    }
    const event = await fetchIncomingCall(callId)
      .unwrap()
      .catch(() => null);
    if (!event || pendingTwilioCall.current !== twilioCall || matchingSocketEvent(twilioCall)) {
      return;
    }
    pendingSocketEvent.current = event;
    pendingSocketEventReceivedAt.current = Date.now();
    setIncomingCall(twilioCall, event);
  };

  const tryPair = () => {
    const twilioCall = pendingTwilioCall.current;
    if (!twilioCall) {
      return;
    }

    const callId = twilioCall.customParameters?.get("callId") ?? "";
    const matchedEvent = matchingSocketEvent(twilioCall);
    const socketEvent: IncomingCallEvent = matchedEvent ?? {
      type: "incoming_call",
      callId,
      fromNumber: twilioCall.parameters.From ?? "Unknown",
      toNumber: "",
      contactName: twilioCall.customParameters?.get("name") ?? undefined,
      contactId: undefined,
    };

    requestCloseMessageDialog();
    setIncomingCall(twilioCall, socketEvent);
    openCallDialog();
    switchToDialerTab();

    if (!matchedEvent && callId) {
      const grace = socketClient.getState().status === "connected" ? INCOMING_FETCH_GRACE_MS : 0;
      setTimeout(() => {
        void rebuildIncomingCard(twilioCall, callId);
      }, grace);
    }
  };

  const clearEndedCall = (callId: string) => {
    if (pendingSocketEvent.current?.callId === callId) {
      pendingSocketEvent.current = null;
    }
    if (heldCallRef.current?.callId === callId) {
      setHeldCall(null);
    }
    if (activeCallInfoRef.current?.callId === callId) {
      activeCallRef.current?.disconnect();
      setActiveCall(null);
      setActiveCallInfo(null);
    }
  };

  const reconcileCall = async (callId: string) => {
    try {
      const call = await dispatch(
        restApi.endpoints.getCall.initiate(callId, { subscribe: false, forceRefetch: true }),
      ).unwrap();
      if (TERMINAL_CALL_STATUSES.includes(call.status)) {
        clearEndedCall(callId);
      }
    } catch (error) {
      console.error("Failed to reconcile the call after a reconnect", error);
    }
  };

  useTwilioDevice(
    (call) => {
      pendingTwilioCall.current = call;
      call.on("accept", () => {
        clearPendingIncoming(call);
        const ringCallId = call.customParameters?.get("callId");
        if (ringCallId) {
          reportCallPlatform({ callId: ringCallId, data: { platform: DEVICE_PLATFORM } });
        }
      });
      call.on("reject", () => clearPendingIncoming(call));
      call.on("disconnect", () => clearPendingIncoming(call));
      call.on("cancel", () => {
        if (clearPendingIncoming(call)) {
          setIncomingCall(null, null);
        }
      });
      tryPair();
    },
    (device) => setDevice(device),
  );

  useA2pStatusSocket();
  useNotificationSocket();

  useCallSocket(
    (event) => {
      pendingSocketEvent.current = event;
      pendingSocketEventReceivedAt.current = Date.now();
      tryPair();
    },
    (event) => {
      refetchCalls();
      if (TERMINAL_CALL_STATUSES.includes(event.status)) {
        clearEndedCall(event.callId);
      }
    },
    (callId) => {
      dispatch(restApi.util.invalidateTags([{ type: "CallParticipants", id: callId }]));
    },
    (event) => {
      toast(`${event.answeredByName ?? "A teammate"} answered`);
    },
  );

  useSocketResync((resync) => {
    if (resync.fullRefresh) {
      dispatch(restApi.util.invalidateTags([...restApiTagTypes]));
      dispatch(adminApi.util.invalidateTags([...adminApiTagTypes]));
    } else {
      dispatch(restApi.util.invalidateTags(["Calls", "CallParticipants"]));
    }
    [activeCallInfoRef.current?.callId, heldCallRef.current?.callId].forEach((callId) => {
      if (callId) {
        void reconcileCall(callId);
      }
    });
    const ringingCall = pendingTwilioCall.current;
    const ringingCallId = ringingCall?.customParameters?.get("callId");
    if (ringingCall && ringingCallId) {
      void rebuildIncomingCard(ringingCall, ringingCallId);
    }
  });

  return (
    <ThemeProvider attribute="class" forcedTheme="light" disableTransitionOnChange>
      <BreadcrumbsProvider key={location.pathname}>
        <SidebarProvider>
          <DashboardSidebar />
          <DashboardWrapper>
            <Outlet />
          </DashboardWrapper>
          <Toaster position="top-right" reverseOrder={false} />
          <HubSpotChatIdentity />
        </SidebarProvider>
      </BreadcrumbsProvider>
      <CallDialogOutlet />
      <MessageDialogOutlet />
    </ThemeProvider>
  );
};

function CallDialogOutlet() {
  const { isCallDialogOpen, closeCallDialog } = useCallDialog();
  if (!isCallDialogOpen) {
    return null;
  }
  return <DraggableCallDialog onClose={closeCallDialog} />;
}

function MessageDialogOutlet() {
  const { isMessageDialogOpen, closeMessageDialog } = useMessageDialog();
  if (!isMessageDialogOpen) {
    return null;
  }
  return <DraggableMessageDialog onClose={closeMessageDialog} />;
}

const DashboardLayout = () => (
  <SocketProvider>
    <PowerDialerProvider>
      <DashboardLayoutInner />
    </PowerDialerProvider>
  </SocketProvider>
);

export default DashboardLayout;
